// The form for a code step's values, built from the fields Claude declared:
//   text / number: one box; choice: a dropdown of rule variants;
//   table: rows the user adds and removes (e.g. one per payment type).
// Every value is optional. The values stay on this device.
import { useState } from "react";
import { Button, Input, InputNumber, Modal, Select } from "antd";
import { Maximize2, Plus, X } from "lucide-react";

const kindOf = (field) => field.kind || "text";

export const emptyValue = (field) => (kindOf(field) === "table" ? [] : "");

// The table's rows: compact in the side panels, roomy in the dialog.
const TableGrid = ({ columns, rows, onChange, large }) => {
  const update = (index, column, text) => onChange(rows.map((row, i) => (i === index ? { ...row, [column]: text } : row)));
  const cell = large ? "px-2 py-1.5" : "px-1.5 py-1";
  return (
    <div className="overflow-x-auto rounded-md border border-gray-200">
      <table className={large ? "w-full text-sm" : "w-full text-[11px]"}>
        <thead className="bg-gray-50">
          <tr>
            {large && <th className="w-8" />}
            {columns.map((column) => (
              <th key={column.name} className={`${cell} text-left font-medium text-gray-500 ${large ? "min-w-[160px] whitespace-nowrap" : ""}`}>
                {column.label || column.name}
              </th>
            ))}
            <th className="w-6" />
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            // Rows have no ids; the index is stable while the user edits a row.
            // eslint-disable-next-line react/no-array-index-key
            <tr key={index} className="border-t border-gray-100">
              {large && <td className="px-2 text-right text-xs text-gray-400">{index + 1}</td>}
              {columns.map((column) => (
                <td key={column.name} className={large ? "p-1" : "p-0.5"}>
                  <Input
                    size={large ? "middle" : "small"}
                    variant={large ? "outlined" : "borderless"}
                    value={row[column.name] ?? ""}
                    onChange={(e) => update(index, column.name, e.target.value)}
                  />
                </td>
              ))}
              <td className="px-1 text-center">
                <button type="button" onClick={() => onChange(rows.filter((_, i) => i !== index))} className="text-gray-400 hover:text-red-600" aria-label="Remove row">
                  <X size={large ? 14 : 11} />
                </button>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr>
              <td colSpan={columns.length + (large ? 2 : 1)} className={`${cell} text-gray-400`}>
                No rows yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const AddRow = ({ rows, onChange, large }) => (
  <button
    type="button"
    onClick={() => onChange([...rows, {}])}
    className={`flex items-center gap-1 text-teal-700 hover:text-teal-900 ${large ? "text-sm" : "text-[11px]"}`}
  >
    <Plus size={large ? 14 : 11} /> Add row
  </button>
);

// The side panels are narrow, so a table can also be filled in a wide
// dialog. Both edit the same rows.
const ValueTable = ({ field, rows, onChange }) => {
  const [open, setOpen] = useState(false);
  const columns = field.columns || [];
  return (
    <div className="space-y-1">
      <TableGrid columns={columns} rows={rows} onChange={onChange} />
      <div className="flex items-center justify-between">
        <AddRow rows={rows} onChange={onChange} />
        <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-800">
          <Maximize2 size={11} /> Open larger
        </button>
      </div>
      <Modal
        open={open}
        title={field.label || field.name.replace(/_/g, " ")}
        onCancel={() => setOpen(false)}
        width="min(1100px, 92vw)"
        destroyOnClose
        footer={
          <Button type="primary" onClick={() => setOpen(false)}>
            Done
          </Button>
        }
      >
        <div className="space-y-3">
          <p className="text-xs text-gray-500">Changes are kept as you type. They stay on your device and are never sent to the AI.</p>
          <div className="max-h-[60vh] overflow-y-auto">
            <TableGrid columns={columns} rows={rows} onChange={onChange} large />
          </div>
          <AddRow rows={rows} onChange={onChange} large />
        </div>
      </Modal>
    </div>
  );
};

export const ValueInput = ({ field, value, onChange }) => {
  switch (kindOf(field)) {
    case "number":
      return <InputNumber size="small" className="!w-full" value={value === "" || value === undefined || value === null ? null : value} onChange={(number) => onChange(number ?? "")} />;
    case "choice":
      return (
        <Select
          size="small"
          className="w-full"
          allowClear
          placeholder="Choose…"
          value={value || undefined}
          onChange={(choice) => onChange(choice ?? "")}
          options={(field.options || []).map((option) => ({ value: option.value, label: option.label || option.value }))}
        />
      );
    case "table":
      return <ValueTable field={field} rows={Array.isArray(value) ? value : []} onChange={onChange} />;
    default:
      return <Input size="small" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  }
};

// fields: [{ name, label, kind, options, columns }]; values: { [name]: value }.
const ValueForm = ({ fields, values = {}, onChange }) => (
  <div className="space-y-2">
    {fields.map((field) => (
      <div key={field.name} className="space-y-0.5 text-[11px] text-gray-600">
        <div>{field.label || field.name.replace(/_/g, " ")}</div>
        <ValueInput field={field} value={values[field.name]} onChange={(value) => onChange(field.name, value)} />
      </div>
    ))}
  </div>
);

export default ValueForm;
